// ./src/utils/api/authErrorHandler.ts
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

// ============================================
// Types
// ============================================

export type AuthErrorCallback = (code: number, message?: string) => void;

// ============================================
// Auth error codes that should trigger redirect
// ============================================

const AUTH_ERROR_CODES = new Set<number>([
  OP_RESULT_CODES.UNAUTHORIZED,
  //OP_RESULT_CODES.FORBIDDEN,
  OP_RESULT_CODES.EXPIRED,
  OP_RESULT_CODES.INVALID_SIGNATURE,
  OP_RESULT_CODES.INVALID_AUDIENCE,
  OP_RESULT_CODES.INVALID_ISSUER,
  OP_RESULT_CODES.MALFORMED_TOKEN,
  OP_RESULT_CODES.NO_SIGNATURE,
]);

// ============================================
// Handler registry (singleton)
// ============================================

let authErrorHandler: AuthErrorCallback | null = null;
let isHandlingError = false;

/**
 * Register the global auth error handler.
 * Call this once at app initialization (in App.tsx).
 */
export const setAuthErrorHandler = (handler: AuthErrorCallback): void => {
  authErrorHandler = handler;
};

/**
 * Clear the auth error handler.
 * Useful for cleanup or testing.
 */
export const clearAuthErrorHandler = (): void => {
  authErrorHandler = null;
  isHandlingError = false;
};

/**
 * Check if a result code indicates an auth error.
 */
export const isAuthErrorCode = (code: number): boolean => {
  return AUTH_ERROR_CODES.has(code);
};

/**
 * Trigger the auth error handler if registered.
 * Prevents multiple simultaneous triggers.
 */
export const triggerAuthError = (code: number, message?: string): void => {
  // Prevent multiple redirects
  if (isHandlingError) {
    return;
  }

  if (authErrorHandler && isAuthErrorCode(code)) {
    isHandlingError = true;
    authErrorHandler(code, message);

    // Reset flag after a delay to allow for navigation
    setTimeout(() => {
      isHandlingError = false;
    }, 2000);
  }
};
/**
 * A `fetch` that keeps an HTTP 401 a 401 whatever its body says. ⚠ Apollo 4 asks for `application/graphql-response+json`
 * first and reads a 401 in that type as a GraphQL ANSWER — the status never reached ApiService, and a session refused that
 * way signed nobody out unless the body also said UNAUTHENTICATED. So a 401 is handed on typed as plain JSON: Apollo then
 * raises it as a `ServerError` carrying the status, which ApiService reads like any other 401.
 * ⚠⚠ ROADMAP #239 — it no longer fires the auth handler itself (it used to): ApiService now REFRESHES an expired session
 *   and repeats the call first, and a sign-out from down here would beat it to it. The handler hears of a 401 from
 *   ApiService alone, and only once a refresh has failed. ▶ check-auth-errors
 */
export const createAuthStatusFetch = (fetchImpl?: typeof fetch): typeof fetch => {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await (fetchImpl ?? fetch)(input, init);
    const type = response.headers.get("content-type") ?? "";

    if (response.status !== 401 || !/graphql-response\+json/i.test(type)) {
      return response;
    }

    const headers = new Headers(response.headers);

    headers.set("content-type", "application/json");

    return new Response(await response.text(), { status: response.status, statusText: response.statusText, headers });
  };
};
